import dayjs from 'dayjs';

/**
 * Helper functions for filter operations
 */

/**
 * Get quick filter date ranges
 */

export const getQuickFilterDates = (type) => {
  const now = dayjs(); // base dayjs instance

  switch (type) {
    case "today": {
      return {
        start: now.clone().startOf("day").toDate(),
        end: now.clone().endOf("day").toDate(),
      };
    }

    case "yesterday": {
      const y = now.clone().subtract(1, "day");
      return {
        start: y.clone().startOf("day").toDate(),
        end: y.clone().endOf("day").toDate(),
      };
    }

    case "last7days": {
      return {
        start: now.clone().subtract(6, "days").startOf("day").toDate(),
        end: now.clone().endOf("day").toDate(),
      };
    }

    case "last30days": {
      return {
        start: now.clone().subtract(29, "days").startOf("day").toDate(),
        end: now.clone().endOf("day").toDate(),
      };
    }

    case "thisMonth": {
      return {
        start: now.clone().startOf("month").toDate(),
        end: now.clone().endOf("month").toDate(),
      };
    }

    case "lastMonth": {
      const lastM = now.clone().subtract(1, "month");
      return {
        start: lastM.clone().startOf("month").toDate(),
        end: lastM.clone().endOf("month").toDate(),
      };
    }

    default:
      return null;
  }
};

/**
 * Convert filters to query params
 */
export const filtersToQueryParams = filters => {
  const params = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;

    if (Array.isArray(value)) {
      params[key] = value.join(',');
    } else if (typeof value === 'object' && value.start && value.end) {
      params[`${key}_start`] = dayjs(value.start).format('YYYY-MM-DD');
      params[`${key}_end`] = dayjs(value.end).format('YYYY-MM-DD');
    } else {
      params[key] = value;
    }
  });

  return params;
};

/**
 * Convert query params to filters
 */
export const queryParamsToFilters = (params, filterConfig) => {
  const filters = {};

  filterConfig.forEach(config => {
    const { id, type } = config;

    if (type === 'dateRange') {
      const start = params[`${id}_start`];
      const end = params[`${id}_end`];
      if (start && end) {
        filters[id] = {
          start: dayjs(start).startOf('day').toDate(),
          end: dayjs(end).endOf('day').toDate(),
        };
      }
    } else if (type === 'select' && config.multiple) {
      const value = params[id];
      if (value) {
        filters[id] = value.split(',');
      }
    } else {
      const value = params[id];
      if (value) {
        filters[id] = value;
      }
    }
  });

  return filters;
};

/**
 * Validate filter values
 */
export const validateFilters = (filters, filterConfig) => {
  const errors = {};

  filterConfig.forEach(config => {
    const value = filters[config.id];
    if (!value) return;

    // Number range validation
    if (config.type === 'numberRange') {
      if (value.min !== undefined && value.max !== undefined) {
        if (Number(value.min) > Number(value.max)) {
          errors[config.id] = 'Min value cannot be greater than max value';
        }
      }
    }

    // Date range validation
    if (config.type === 'dateRange') {
      if (value.start && value.end) {
        if (dayjs(value.start).isAfter(dayjs(value.end))) {
          errors[config.id] = 'Start date cannot be after end date';
        }
      }
    }

    // Custom validation
    if (config.validate) {
      const error = config.validate(value);
      if (error) {
        errors[config.id] = error;
      }
    }
  });

  return errors;
};

/**
 * Check if filters are empty
 */
export const areFiltersEmpty = filters => {
  return Object.values(filters).every(value => {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && !value.start && !value.end) return true;
    return false;
  });
};

/**
 * Get active filter count
 */
export const getActiveFilterCount = filters => {
  return Object.values(filters).filter(value => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'object' && !value.start && !value.end) return false;
    return true;
  }).length;
};
